import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { EmailComposer } from "capacitor-email-composer";
import axiosInstance from "./axios";

/**
 * Native-only email util for Capacitor apps.
 *
 * Opens the OS email composer directly with an optional attachment:
 *  - iOS:      in-app `MFMailComposeViewController`
 *  - Android:  email intent (filtered to mail apps only) via FileProvider
 *
 * This util DOES NOT work on web. For the web/browser experience use the
 * `mailto:` based util (`openSystemMailer` in quotationApis.ts).
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Converts a Blob (File) to a base64 data string (no data: prefix). */
const blobToBase64 = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
};

/**
 * Ensures the attachment file name has an extension so mail apps recognize the
 * file type. Defaults to `.pdf` when it cannot be determined — this keeps every
 * mail app attaching the quotation PDF correctly.
 */
const ensureFileExtension = (fileName: string, mimeType?: string) => {
  const clean = fileName.trim() || "attachment";
  if (/\.[a-z0-9]{1,5}$/i.test(clean)) return clean;
  const ext = (mimeType && EXTENSION_BY_MIME[mimeType]) || "pdf";
  return `${clean}.${ext}`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CapacitorMailAttachment {
  /** A pre-existing `file://` URI on the device (taken as-is) */
  uri?: string;
  /** File data to attach — written to the device cache before opening */
  blob?: Blob;
  /** Attachment file name (extension recommended) */
  fileName?: string;
}

export interface OpenCapacitorMailerOptions {
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  /** Indicates whether the body is HTML (primarily iOS) */
  isHtml?: boolean;
  attachment?: CapacitorMailAttachment;
}

// ─── Main util ───────────────────────────────────────────────────────────────

/**
 * Opens the native e-mail composer with the provided details and attachment.
 *
 * Resolves once the user finishes/dismisses the composer.
 * Rejects when:
 *  - called on web (`Capacitor.isNativePlatform()` false)
 *  - no mail account is configured on the device (iOS)
 *  - writing the attachment / building the intent fails
 *
 * Callers should catch and fall back to the web `mailto:` util when needed.
 */
export const openCapacitorEmailComposer = async (
  options: OpenCapacitorMailerOptions,
): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    throw new Error(
      "[emailCapacitor] openCapacitorEmailComposer is only available on native Capacitor platforms. Use the web (mailto) mailer util instead.",
    );
  }

  let attachmentPath: string | null = null;

  if (options.attachment?.uri) {
    attachmentPath = options.attachment.uri;
  } else if (options.attachment?.blob) {
    const base64Data = await blobToBase64(options.attachment.blob);
    const fileName = ensureFileExtension(
      options.attachment.fileName || "attachment",
      options.attachment.blob.type,
    );

    const savedFile = await Filesystem.writeFile({
      path: `${Date.now()}_${fileName}`,
      data: base64Data,
      directory: Directory.Cache,
    });
    attachmentPath = savedFile.uri;
  }

  // Only meaningful on iOS (Android always returns true).
  // A reject here lets callers fall back to the mailto util.
  const { hasAccount } = await EmailComposer.hasAccount();
  if (!hasAccount) {
    throw new Error(
      "[emailCapacitor] No mail account is configured on this device.",
    );
  }

  await EmailComposer.open({
    to: options.to ? [options.to] : [],
    cc: options.cc ? [options.cc] : [],
    bcc: options.bcc ? [options.bcc] : [],
    subject: options.subject || "",
    body: options.body || "",
    isHtml: options.isHtml ?? false,
    attachments: attachmentPath
      ? [{ type: "absolute", path: attachmentPath.replace(/^file:\/\//, "") }]
      : [],
  });
};

export interface QuoteEmailData {
  to?: string;
  cc?: string;
  bcc?: string;
  mail_subject?: string;
  html_template?: string;
  pdf_url?: string;
  pdf_name?: string;
}

/**
 * Opens the native mail composer with a quotation PDF attached.
 *
 * Fetches the quotation PDF blob (same endpoint as the PDF exporter), passes it
 * to `openCapacitorEmailComposer` and awaits the composer being dismissed.
 *
 * Only intended for Capacitor apps — callers should guard with
 * `Capacitor.isNativePlatform()`.
 */
export const openQuoteEmailWithPdf = async (
  quoteId: string,
  emailData: QuoteEmailData,
): Promise<void> => {
  const response = await axiosInstance.post(
    `/quotes/generate-pdf`,
    { quote_id: quoteId },
    { responseType: "blob" },
  );

  const blob = new Blob([response.data], { type: "application/pdf" });

  await openCapacitorEmailComposer({
    to: emailData?.to,
    cc: emailData?.cc,
    bcc: emailData?.bcc,
    subject: emailData?.mail_subject,
    body: emailData?.html_template,
    isHtml: true,
    attachment: { blob, fileName: emailData?.pdf_name || "Quotation.pdf" },
  });
};

export default openCapacitorEmailComposer;
